import { useCallback, useState, type ReactNode } from "react";

/** Styled confirmation (U12) — replaces raw `window.confirm()` (5 sites), which
 *  is unbrandable, blocks the main thread, and looks broken to customers.
 *
 *  const { ask, dialog } = useConfirm();
 *  ...
 *  <button onClick={() => ask("Delete firmware v4?", () => removeFw.mutate(id))} />
 *  ...
 *  {dialog}   // render once, anywhere in the page
 */
export function useConfirm(): {
  ask: (message: ReactNode, onConfirm: () => void, confirmLabel?: string) => void;
  dialog: ReactNode;
} {
  const [state, setState] = useState<{ message: ReactNode; onConfirm: () => void; confirmLabel: string } | null>(null);
  const ask = useCallback((message: ReactNode, onConfirm: () => void, confirmLabel = "Confirm") => {
    setState({ message, onConfirm, confirmLabel });
  }, []);

  const dialog = state ? (
    <>
      <div onClick={() => setState(null)}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,32,.45)", zIndex: 1100 }} />
      <div role="dialog" aria-modal="true"
        style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1101,
          background: "var(--surface)", border: "1px solid var(--line2)", borderRadius: 14,
          boxShadow: "0 8px 40px rgba(0,0,0,.35)", padding: "20px 22px", width: "min(420px, 92vw)" }}>
        <div style={{ fontSize: 13, color: "var(--t-bright)", lineHeight: 1.5, whiteSpace: "pre-line" }}>
          {state.message}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn ghost" onClick={() => setState(null)}>Cancel</button>
          <button className="btn teal" autoFocus
            onClick={() => { const fn = state.onConfirm; setState(null); fn(); }}>
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </>
  ) : null;

  return { ask, dialog };
}
